import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { box, button, input, label } from '../shared/styles';
import { fullFactorial } from './significance';
import { unitCheck, unitToken } from './units';

/*
 * Design an experiment: its factors and their levels, one arm per combination
 * (the full factorial), replicates, what is measured, and the analysis plan.
 * Keys are short lower-case tokens (t25, temperature): they are what the
 * statistics and any AI synthesis see, never the labels.
 */
const TOKEN = /^[a-z][a-z0-9_]{0,39}$/;
const emptyFactor = { key: '', label: '', unit: '', levelsText: '' };
const emptyOutcome = { key: '', label: '', unit: '', type: 'continuous' };

function levelsOf(text) {
  return text.split(',').map((s) => s.trim()).filter(Boolean).map((k) => ({ key: k, label: k }));
}

function UnitNote({ raw, t, testId }) {
  const { token, changed, invalid } = unitCheck(raw);
  if (!changed && !invalid) return null;
  return (
    <span data-testid={testId} style={{ fontSize: 12, alignSelf: 'center',
      color: invalid ? 'var(--c-danger-fg)' : 'var(--c-warning-fg)' }}>
      {invalid ? t('unitInvalid') : t('unitSavedAs', { token })}
    </span>
  );
}

export default function ExperimentDesigner({ onSave }) {
  const { t } = useTranslation('experiments');
  const [title, setTitle] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [taxon, setTaxon] = useState('');
  const [kind, setKind] = useState('independent');
  const [factors, setFactors] = useState([{ ...emptyFactor }]);
  const [replicates, setReplicates] = useState(4);
  const [outcomes, setOutcomes] = useState([{ ...emptyOutcome }]);
  const [alpha, setAlpha] = useState(0.05);
  const [correction, setCorrection] = useState('holm');
  const [error, setError] = useState('');

  const built = factors.map((f) => ({ key: f.key.trim(), label: f.label.trim() || f.key.trim(),
    unit: unitToken(f.unit) || null, levels: levelsOf(f.levelsText) }));
  // A unit is saved as a token ("°C" -> "c"); one with nothing usable left blocks saving.
  const badUnit = [...factors, ...outcomes].some((x) => unitCheck(x.unit).invalid);
  const arms = fullFactorial(built).map((a) => ({ ...a, replicates: Number(replicates) }));
  const badToken = [...built.map((f) => f.key), ...built.flatMap((f) => f.levels.map((l) => l.key)),
    ...outcomes.map((o) => o.key.trim())].some((k) => !TOKEN.test(k));

  const setAt = (list, setter, i, patch) => setter(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await onSave({
        title, hypothesis: hypothesis || null, subject_taxon: taxon || null, design_kind: kind,
        factors: built, arms,
        outcomes: outcomes.map((o) => ({ key: o.key.trim(), label: o.label.trim() || o.key.trim(),
          unit: unitToken(o.unit) || null, type: o.type })),
        plan: { alpha: Number(alpha), correction, outcomes: outcomes.map((o) => o.key.trim()) },
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || String(err));
    }
  };

  return (
    <form onSubmit={submit} style={box} data-testid="designer">
      <h3 style={{ marginTop: 0 }}>{t('newExperiment')}</h3>
      <label style={label}>{t('title')}
        <input style={input} value={title} onChange={(e) => setTitle(e.target.value)} title={t('tip.title')} data-testid="exp-title" required />
      </label>
      <label style={label}>{t('hypothesis')}
        <textarea style={input} rows={2} value={hypothesis} onChange={(e) => setHypothesis(e.target.value)} title={t('tip.hypothesis')} />
      </label>
      <label style={label}>{t('subjectTaxon')}
        <input style={input} value={taxon} onChange={(e) => setTaxon(e.target.value)} title={t('tip.subjectTaxon')} data-testid="exp-taxon" />
      </label>
      <label style={label}>{t('designKind')}
        <select style={input} value={kind} onChange={(e) => setKind(e.target.value)} title={t('tip.designKind')} data-testid="exp-kind">
          <option value="independent">{t('kind_independent')}</option>
          <option value="blocked">{t('kind_blocked')}</option>
          <option value="repeated">{t('kind_repeated')}</option>
        </select>
      </label>

      <h4>{t('factors')}</h4>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0, fontSize: 13 }}>{t('tokenHelp')}</p>
      {factors.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input style={input} placeholder={t('factorKey')} value={f.key} title={t('tip.factorKey')} data-testid={`factor-key-${i}`}
            onChange={(e) => setAt(factors, setFactors, i, { key: e.target.value })} />
          <input style={input} placeholder={t('factorLabel')} value={f.label} title={t('tip.factorLabel')}
            onChange={(e) => setAt(factors, setFactors, i, { label: e.target.value })} />
          <input style={input} placeholder={t('unit')} value={f.unit} size={6} title={t('tip.factorUnit')}
            data-testid={`factor-unit-${i}`} onChange={(e) => setAt(factors, setFactors, i, { unit: e.target.value })} />
          <UnitNote raw={f.unit} t={t} testId={`factor-unit-note-${i}`} />
          <input style={{ ...input, flex: 1 }} placeholder={t('levelsPlaceholder')} value={f.levelsText} title={t('tip.factorLevels')}
            data-testid={`factor-levels-${i}`}
            onChange={(e) => setAt(factors, setFactors, i, { levelsText: e.target.value })} />
        </div>
      ))}
      {factors.length < 2 && (
        <button type="button" onClick={() => setFactors([...factors, { ...emptyFactor }])} title={t('tip.addFactor')}>{t('addFactor')}</button>
      )}
      <label style={{ ...label, marginTop: 10 }}>{t('replicates')}
        <input style={input} type="number" min={1} max={1000} value={replicates} title={t('tip.replicates')} data-testid="exp-replicates"
          required onChange={(e) => setReplicates(e.target.value)} />
      </label>
      <p data-testid="arm-preview" style={{ fontSize: 13 }}>
        {t('armsPreview')}: {arms.map((a) => a.key).filter(Boolean).join(', ') || '—'}
      </p>

      <h4>{t('outcomes')}</h4>
      {outcomes.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <input style={input} placeholder={t('outcomeKey')} value={o.key} title={t('tip.outcomeKey')} data-testid={`outcome-key-${i}`}
            onChange={(e) => setAt(outcomes, setOutcomes, i, { key: e.target.value })} />
          <input style={input} placeholder={t('outcomeLabel')} value={o.label} title={t('tip.outcomeLabel')}
            onChange={(e) => setAt(outcomes, setOutcomes, i, { label: e.target.value })} />
          <input style={input} placeholder={t('unit')} value={o.unit} size={6} title={t('tip.outcomeUnit')}
            data-testid={`outcome-unit-${i}`} onChange={(e) => setAt(outcomes, setOutcomes, i, { unit: e.target.value })} />
          <UnitNote raw={o.unit} t={t} testId={`outcome-unit-note-${i}`} />
          <select style={input} value={o.type} title={t('tip.outcomeType')} onChange={(e) => setAt(outcomes, setOutcomes, i, { type: e.target.value })}>
            {['continuous', 'count', 'binary', 'ordinal'].map((k) => <option key={k} value={k}>{t(`type_${k}`)}</option>)}
          </select>
        </div>
      ))}
      <button type="button" onClick={() => setOutcomes([...outcomes, { ...emptyOutcome }])} title={t('tip.addOutcome')}>{t('addOutcome')}</button>

      <h4>{t('plan')}</h4>
      <div style={{ display: 'flex', gap: 12 }}>
        <label style={label}>{t('alpha')}
          <input style={input} type="number" step="any" min="0.001" max="0.2" value={alpha}
            onChange={(e) => setAlpha(e.target.value)} title={t('tip.alpha')} />
        </label>
        <label style={label}>{t('correction')}
          <select style={input} value={correction} title={t('tip.correction')} onChange={(e) => setCorrection(e.target.value)}>
            <option value="holm">{t('correction_holm')}</option>
            <option value="none">{t('correction_none')}</option>
          </select>
        </label>
      </div>
      {badToken && <p style={{ color: 'var(--c-warning-fg)', fontSize: 13 }}>{t('tokenHelp')}</p>}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <button type="submit" style={button('var(--c-primary)')} data-testid="exp-save" title={t('tip.saveDesign')}
        disabled={badToken || badUnit}>
        {t('saveDesign')}
      </button>
    </form>
  );
}
